/*
 * Anti-slip underlay cart linking.
 *
 * The underlay is a real cart line item, added with a hidden `_underlay_for`
 * line-item property holding the *parent* variant id it is attached to. It is
 * hidden from the rendered cart line list (the theme `{% continue %}`s over any
 * line with that property) and surfaced instead by the "Anti-slip underlay" card
 * under its parent.
 *
 * Rules enforced here:
 *  - Each parent variant has at most one attached underlay line.
 *  - The underlay line quantity always tracks its parent's quantity.
 *  - Changing the parent quantity (or removing it) updates/removes the underlay
 *    in the same cart request.
 *
 * Hooks (data attributes):
 *  - [data-cart-addon]                 card wrapper, carries the link metadata:
 *      data-underlay-variant-id, data-parent-variant-id, data-parent-qty,
 *      data-underlay-key (present only when already added)
 *  - [data-cart-addon-add]             add the matching underlay for this parent
 *  - [data-cart-addon-remove]          remove the attached underlay
 *  - [data-underlay-qty-form]          a parent quantity form; data-underlay-key
 *                                      names the attached underlay line to sync
 */
(function () {
  const UNDERLAY_PROP = '_underlay_for';

  // Market-prefixed cart endpoints. A bare '/cart/add.js' from an /en-fr page
  // resolves in the default market, so the drawer can come back with GBP pricing.
  const cartRoute = (key, fallback) => (window.routes && window.routes[key]) || fallback;
  const CART_URL = cartRoute('cart_url', '/cart');
  const CART_ADD_URL = cartRoute('cart_add_url', '/cart/add');
  const CART_UPDATE_URL = cartRoute('cart_update_url', '/cart/update');

  // How long to wait after the last write before re-rendering. A page reload (or
  // drawer refresh) fired the instant one click lands would abort the fetch of a
  // click made a moment later, so give a fast clicker room to land the next one.
  const RERENDER_DELAY = 300;

  const postJSON = (url, body) =>
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });

  const getCart = () => fetch(`${CART_URL}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());

  // Fresh server markup is about to replace what's on screen, so anything we
  // were tracking about in-flight clicks is superseded by it.
  const refresh = (open) => {
    pendingQty.clear();
    sentQty.clear();
    return document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: !!open } }));
  };

  // Add `quantity` of the underlay variant, linked to `parentVariantId`.
  // Resolves to the created/merged line item (so callers can track its key).
  async function addUnderlay(underlayVariantId, parentVariantId, quantity) {
    const res = await postJSON(`${CART_ADD_URL}.js`, {
      id: Number(underlayVariantId),
      quantity: Math.max(1, quantity || 1),
      properties: { [UNDERLAY_PROP]: String(parentVariantId) },
    });
    if (!res.ok) throw new Error('underlay add failed');
    return res.json();
  }

  // updates: { "<lineKey>": qty, ... } - qty 0 removes the line.
  async function updateLines(updates) {
    if (!updates || !Object.keys(updates).length) return null;
    const res = await postJSON(`${CART_UPDATE_URL}.js`, { updates });
    if (!res.ok) throw new Error('underlay update failed');
    return res.json();
  }

  // Keep every underlay line consistent with its parent:
  //  - quantity tracks the parent's quantity;
  //  - an *orphan* (parent variant no longer in the cart) is removed outright.
  //
  // An underlay is only ever added together with its rug, so a parentless
  // underlay is always stale - it means the rug was removed by a path that
  // didn't sync the pair (no-JS form fallback, the submit interceptor's error
  // fallback, a third-party cart app, or checkout-side removal). This is the
  // safety net that guarantees an underlay never lingers in the cart alone; the
  // quantity-form interceptor below still removes the pair synchronously for the
  // common in-cart removal so the customer sees it happen immediately.
  // Queued as a single unit: the read and the write it derives must not have a
  // quantity change land between them, or the sweep acts on a stale cart.
  function reconcile() {
    return enqueue(reconcileNow);
  }

  async function reconcileNow() {
    const cart = await getCart();
    const parentQty = {};
    cart.items.forEach((it) => {
      if (!(it.properties && it.properties[UNDERLAY_PROP])) {
        parentQty[it.variant_id] = (parentQty[it.variant_id] || 0) + it.quantity;
      }
    });
    const updates = {};
    cart.items.forEach((it) => {
      const link = it.properties && it.properties[UNDERLAY_PROP];
      if (!link) return;
      const want = parentQty[link];
      if (want == null) {
        updates[it.key] = 0; // parent gone → drop the orphaned underlay
      } else if (want !== it.quantity) {
        updates[it.key] = want; // keep the underlay qty tracking its parent
      }
    });
    if (Object.keys(updates).length) {
      await updateLines(updates);
      return true;
    }
    return false;
  }

  // ----- Serialised cart writes ---------------------------------------------
  //
  // Shopify's cart endpoints must not be hit concurrently, and a write still in
  // flight when we re-render is aborted by the navigation. Either one rejects
  // the request, so every mutation goes through one queue and the re-render is
  // held back until the queue has drained.

  let queue = Promise.resolve();
  let queued = 0;
  let rerenderTimer = null;
  let rerenderWanted = false;

  function enqueue(task) {
    queued += 1;
    if (rerenderTimer) {
      clearTimeout(rerenderTimer);
      rerenderTimer = null;
    }
    // Run regardless of whether the previous task settled or failed - one bad
    // write must not wedge the queue for every click after it.
    const result = queue.then(task, task);
    const done = () => {
      queued -= 1;
      // The task that asked for the re-render may well have finished ahead of
      // one queued behind it, so re-arm here rather than lose the request.
      if (queued === 0 && rerenderWanted) scheduleRerender();
    };
    queue = result.then(done, done);
    return result;
  }

  // Reload the standalone cart page, or refresh the drawer everywhere else.
  function rerender() {
    rerenderWanted = false;
    if (document.querySelector('[data-main-cart]')) window.location.reload();
    else refresh(false); // clears the pending-click state on our behalf
  }

  // Re-render once the burst of clicks is over. Safe to call after a failed
  // write too: re-rendering resyncs the cart with the server, which is what a
  // failure needs anyway.
  function scheduleRerender() {
    rerenderWanted = true;
    if (rerenderTimer) clearTimeout(rerenderTimer);
    if (queued > 0) return; // a later write is still pending; it will re-arm this
    rerenderTimer = setTimeout(() => {
      rerenderTimer = null;
      if (queued === 0) rerender();
    }, RERENDER_DELAY);
  }

  // ----- Cart card: Add / Remove (delegated, survives drawer re-renders) -----
  document.addEventListener('click', async (e) => {
    const addBtn = e.target.closest('[data-cart-addon-add]');
    if (addBtn) {
      const card = addBtn.closest('[data-cart-addon]');
      const underlayId = card && card.dataset.underlayVariantId;
      if (!card || !underlayId) return;
      e.preventDefault();
      addBtn.disabled = true;
      try {
        await enqueue(() =>
          addUnderlay(underlayId, card.dataset.parentVariantId, parseInt(card.dataset.parentQty, 10) || 1)
        );
        scheduleRerender();
      } catch (err) {
        addBtn.disabled = false;
      }
      return;
    }

    const removeBtn = e.target.closest('[data-cart-addon-remove]');
    if (removeBtn) {
      const card = removeBtn.closest('[data-cart-addon]');
      const key = card && card.dataset.underlayKey;
      if (!key) return;
      e.preventDefault();
      removeBtn.disabled = true;
      try {
        await enqueue(() => updateLines({ [key]: 0 }));
        scheduleRerender();
      } catch (err) {
        removeBtn.disabled = false;
      }
    }
  });

  // ----- Parent quantity change: update parent + its underlay together -----

  // Latest quantity the customer has asked for, per line key. Lets a burst of
  // clicks accumulate instead of each one replaying the same absolute quantity.
  // Cleared on re-render, when the server's numbers become the truth again.
  const pendingQty = new Map();

  // Latest quantity actually sent, per line key. Writes are serialised, so by
  // the time a queued click runs the customer has often clicked past it; those
  // send nothing rather than making the burst wait on a redundant round trip.
  const sentQty = new Map();

  // Resolved when the write runs, not when the click happened, so it always
  // carries the customer's latest intent.
  function writeQuantities(keys) {
    const updates = {};
    keys.forEach((key) => {
      const want = pendingQty.get(key);
      if (want !== undefined && sentQty.get(key) !== want) updates[key] = want;
    });
    const changing = Object.keys(updates);
    if (!changing.length) return Promise.resolve(null); // superseded by a later click
    changing.forEach((key) => sentQty.set(key, updates[key]));
    return updateLines(updates).catch((err) => {
      // It never landed, so don't let it suppress a retry of the same quantity.
      changing.forEach((key) => sentQty.delete(key));
      throw err;
    });
  }

  // Keep the number between the steppers in step with the clicks, since the
  // markup isn't re-rendered until the burst ends.
  function setDisplayedQty(key, qty) {
    document.querySelectorAll('[data-line-qty-display]').forEach((el) => {
      if (el.dataset.lineQtyDisplay === key) el.textContent = String(qty);
    });
  }

  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-underlay-qty-form]');
    if (!form) return;
    const idInput = form.querySelector('input[name="id"]');
    const submitter = e.submitter || form.querySelector('button[name="quantity"]');
    if (!idInput || !submitter) return;
    const requested = parseInt(submitter.value, 10);
    if (Number.isNaN(requested)) return;

    e.preventDefault();

    const key = idInput.value;
    const rendered = parseInt(form.dataset.lineQty, 10);
    let qty = requested;
    if (!Number.isNaN(rendered)) {
      // The button's value was baked in when the line was rendered, so treat it
      // as a delta against that and re-apply it to the latest pending quantity.
      // Three quick taps on "−" then step down by three, not by one.
      const current = pendingQty.has(key) ? pendingQty.get(key) : rendered;
      qty = Math.max(0, current + (requested - rendered));
    }
    pendingQty.set(key, qty);
    setDisplayedQty(key, qty);

    const keys = [key];
    const underlayKey = form.dataset.underlayKey;
    if (underlayKey) {
      pendingQty.set(underlayKey, qty); // qty 0 removes both
      keys.push(underlayKey);
    }

    // No native form.submit() fallback here: posting to /cart/change with a key
    // an earlier click already removed lands the customer on Shopify's error
    // page. Re-rendering shows them the real cart instead.
    enqueue(() => writeQuantities(keys))
      .catch(() => {})
      .then(scheduleRerender);
  });

  // Safety net: correct any drift on load - a parent qty edited in a context we
  // didn't intercept, or an underlay orphaned because its rug was removed outside
  // the interceptor. If anything changed, re-render so the visible cart (and its
  // totals/count) reflect the cleanup.
  document.addEventListener('DOMContentLoaded', () => {
    reconcile()
      .then((changed) => {
        if (changed) rerender();
      })
      .catch(() => {});
  });

  // The drawer is the primary cart surface and can be opened without a page load,
  // so sweep for orphans each time it opens - this catches removals made by paths
  // that never reload (e.g. a third-party cart app deleting the rug). Only the
  // drawer markup is refreshed here, and only when the sweep actually changed the
  // cart, so a clean cart opens with no extra work.
  document.addEventListener('cart-drawer:open', () => {
    reconcile()
      .then((changed) => {
        if (changed) refresh(false);
      })
      .catch(() => {});
  });

  window.UnderlayCart = { addUnderlay, updateLines, reconcile, refresh, getCart };
})();
